/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useContext, useEffect, useRef } from 'react'
import moment from 'moment'
import {
  Form,
  Input,
  Button,
  Divider,
  Drawer,
  Select,
  DatePicker,
  Row,
  Col,
  Table,
  Tag,
  Empty,
} from 'antd'
import { PromoTag } from './promo'
import { ArrowRightOutlined, PlusOutlined } from '@ant-design/icons'
import { ErpClientContext } from '../../services/erp'
import * as dayjs from 'dayjs'
import { message } from 'antd'
import { OffersContext, isExpired } from './context'
import { OffersForm } from './form'
import 'antd/dist/antd.css'

const initial = {
  q: undefined,
  date: moment(),
  id_tipo: undefined,
  p: 1,
}

export function Offers() {
  const erpService = useContext(ErpClientContext)
  const offersCtx = useContext(OffersContext)
  const [form] = Form.useForm()
  const lastSearch = useRef()
  const [loading, setLoading] = useState(true)
  const [formState, setFormState] = useState(initial)
  const [data, setData] = useState(undefined)
  const [drawer, showDrawer] = useState(false)

  offersCtx.onUpdate = (data) => {
    setFormState({ ...formState, refresh: Math.random() })
  }

  async function onSearch(params) {
    const date = params.date || formState.date
    const last = lastSearch.current || {}
    const data = {
      ...formState,
      ...params,
      date: date ? date.milliseconds(0).toISOString() : undefined,
    }

    if (
      (last.q === data.q || !data.q) &&
      last.date === data.date &&
      last.tipo === data.tipo
    ) {
      return
    }

    lastSearch.current = data

    setLoading(true)
    const resp = await erpService.fetch('/offers/@search', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    if (resp.status === 200) {
      const d = await resp.json()
      setData(d)
    } else {
      setData()
    }
    setLoading(false)
  }

  useEffect(() => {
    onSearch({})
  }, [])

  return (
    <div
      className="container"
      style={{ backgroundColor: '#fff', padding: '30px' }}
    >
      <div className="level-left">
        <h2 className="title is-size-5">Offers</h2>
      </div>
      <Drawer
        title="Add a new offer"
        width={600}
        onClose={() => showDrawer(false)}
        visible={drawer}
        bodyStyle={{ paddingBottom: 80 }}
      >
        <OffersForm
          key={`offers-form-${drawer}`}
          onClose={() => showDrawer(false)}
        />
      </Drawer>
      <Divider />
      <Form
        layout={'inline'}
        form={form}
        onValuesChange={(changedFields, allFields) => setFormState(allFields)}
      >
        <Form.Item label="Search" name="q">
          <Input
            onBlur={(e) => onSearch({ q: e.target.value })}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                onSearch({ q: e.target.value })
              }
            }}
            placeholder="name or id_producto"
          />
        </Form.Item>
        <Form.Item label="Type" name="tipo">
          <Select
            onChange={(tipo) => onSearch({ tipo })}
            style={{ width: 120 }}
          >
            <Select.Option>All</Select.Option>
            <Select.Option value="o">Offer</Select.Option>
            <Select.Option value="p">Promotion</Select.Option>
            <Select.Option value="e">Special Price</Select.Option>
            <Select.Option value="m">Mix 6</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item label="Date" name="date">
          <DatePicker
            onChange={(date) => onSearch({ date })}
            defaultValue={initial.date}
          />
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            onClick={() => showDrawer(true)}
            icon={<PlusOutlined />}
          >
            Add Offer
          </Button>
        </Form.Item>
      </Form>
      <Divider />
      <div>
        {loading && <div>Loading...</div>}
        {!loading && !data && <Empty />}
        {!loading && data && (
          <Table
            dataSource={data.result}
            columns={columns}
            rowKey={(record) => record.id}
            pagination={{
              current: data.page,
              pageSize: data.per_page,
              total: data.items,
              position: ['topLeft', 'bottomRight'],
              showSizeChanger: false,
            }}
            onChange={(pagination, filters, sorter) => {
              setFormState({ ...formState, p: pagination.current })
            }}
            size="small"
          />
        )}
      </div>
    </div>
  )
}

const renderDate = (val) => dayjs(val).format('DD/MM/YYYY HH:mm')
const tipos = {
  o: 'Offer',
  p: 'Promotion',
  e: 'Special Price',
  m: 'Mix 6',
}

const renderPromo = (text, record) => {
  return <PromoTag record={record} />
}

const rowDate = (text, record) => {
  const colors = {
    expired: ['#BBB', '#BBB'],
    current: ['#2db7f5', '#108ee9'],
  }

  const status = isExpired(record) ? 'expired' : 'current'

  return (
    <>
      <Tag color={colors[status][0]}>{renderDate(record.fecha_desde)}</Tag>
      <ArrowRightOutlined />
      &nbsp;
      <Tag color={colors[status][1]}>{renderDate(record.fecha_hasta)}</Tag>
    </>
  )
}

const rowActions = (text, record) => {
  return <>{!isExpired(record) && <Expire record={record} />}</>
}

const Expire = ({ record }) => {
  const isFuture = new Date(record.fecha_desde) > new Date()
  const ctx = useContext(OffersContext)
  const erpService = useContext(ErpClientContext)

  const doExpire = async () => {
    const endpoint = isFuture
      ? `/offers/${record.id}`
      : `/offers/${record.id}/@expire`
    let res = await erpService.fetch(endpoint, {
      method: isFuture ? 'DELETE' : 'POST',
    })
    if (res.status === 200) {
      const verb = isFuture ? 'removed' : 'expired'
      message.success(`Offer ${verb}`)
      let data = await res.json()
      ctx.onUpdate(data)
    } else {
      let error = await res.json()
      const verb = isFuture ? 'removing' : 'expiring'
      message.error(`Failed ${verb} row ${JSON.stringify(error)}`)
    }
  }

  return (
    <Button type="dashed" size="small" onClick={() => doExpire()}>
      {isFuture ? 'Remove' : 'Expire'}
    </Button>
  )
}

const columns = [
  {
    title: 'Type',
    dataIndex: 'id_tipo',
    key: 'id_tipo',
    render: (o) => tipos[o],
  },
  {
    title: 'Promo',
    key: 'promo',
    render: renderPromo,
  },
  {
    title: 'Product ID',
    dataIndex: 'id_producto',
    key: 'id_producto',
  },
  {
    title: 'Group',
    dataIndex: 'grupo',
    key: 'grupo',
  },
  {
    title: 'Dates',
    key: 'fecha_desde',
    render: rowDate,
  },
  {
    title: 'Actions',
    key: 'actions',
    render: rowActions,
  },
]

export function Offer({ item }) {
  return (
    <Row>
      <Col span={20}></Col>
      <Col span={4}>Actions</Col>
    </Row>
  )
}
